import { z } from 'zod';

/**
 * All environment variables consumed by the app are declared and validated
 * here. Fail fast on boot if the runtime environment is misconfigured
 * rather than surfacing cryptic errors deep inside a network call.
 */
const envSchema = z.object({
  /**
   * Either an absolute URL (calling the backend directly) or a
   * same-origin path such as `/api/v1` (reverse-proxied by the Vite dev
   * server or the production Nginx config) - the latter is preferred so
   * the browser never makes a cross-origin request and the backend
   * doesn't need CORS configured at all.
   */
  VITE_API_BASE_URL: z.string().refine((value) => value.startsWith('/') || /^https?:\/\//.test(value), {
    message: 'VITE_API_BASE_URL must be an absolute URL or a path starting with "/"',
  }),
  VITE_API_TIMEOUT: z.coerce.number().int().positive().default(15000),
  VITE_APP_NAME: z.string().min(1).default('Quiz Admin Portal'),
});

function parseEnv() {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const issues = result.error.issues.map((issue) => `- ${issue.path.join('.')}: ${issue.message}`).join('\n');
    throw new Error(`Invalid environment configuration:\n${issues}`);
  }

  return result.data;
}

export const env = parseEnv();
